import { z } from 'zod'
import { getDistance, getCompassDirection } from 'geolib'
import { createInlineTool } from '@onxmaps/ai-toolkit'
import { getApolloClient } from '@/lib/apollo-client'
import { reportAiError } from '@/lib/ai/lib/report'
import { sqMetersToAcres } from '@/lib/area'
import { getBestFitUnitOfLength, type UnitType } from '@/lib/math'
import { getItem } from '@/plugins/storage'
import { isPointInView, queryCoord } from '@/services/map-service'
import { buildWaterbodyUrl } from '@/fish/lib/ai/navigation'
import { getWaterbodiesByFilter } from '@/fish/lib/graph/queries/waterbodies-gql'
import {
  buildWaterbodyFilter,
  type PotentialCategory,
} from '@/fish/lib/ai/lib/waterbody-filter'
import type { ToolRegistryEntry } from '@onxmaps/ai-toolkit/toolbox'
import type { AiDependencies } from '@/lib/ai/lib/dependencies'

export const NAME = 'queryWaterbodies'

const LIMIT = 10

const DESCRIPTION = `Look up onX's own lake/waterbody data filtered by fish species and/or distance from a location, e.g. "a good walleye lake within 75 miles." Use this instead of web_search for questions about species, size, or location matches against onX's own waterbody catalog. Use findPlaces instead when the user just wants nearby waterbodies with no species/size filter.

"potential" narrows a species match to a specific fish-attribute dimension, matching onX's own map-filter categories — use whichever fits the user's phrasing (multiple may apply at once):
- "trophy": trophy-sized fish have been surveyed (big fish, "trophy potential")
- "abundant": high abundance of the species ("lots of", "loaded with")
- "keeper": keeper-sized fish present (eating-size, "keepers")
- "highStockingEffort": heavily/recently stocked ("well-stocked", "put-and-take")
Omit "potential" for a plain species-presence match with no particular attribute in mind.

Each result's "potentialMatches" reflects only the categories actually requested — do not assume or invent categories that weren't asked for.

Each result includes a "url" — present it as an ordinary Markdown link using the lake's own name as the link text, e.g. [Lake Name](url), on its own line with no other text before, after, or alongside it — not even on a following line. The app replaces it with an interactive card showing that lake's distance, direction, area, and potential-match details automatically, so do not restate any of those figures yourself anywhere in your response.`

const POTENTIAL_LABELS: Record<PotentialCategory, string> = {
  trophy: 'Trophy Potential',
  abundant: 'High Abundance',
  keeper: 'Keeper Potential',
  highStockingEffort: 'High Stocking Effort',
}

interface Anchor {
  lat: number
  lon: number
}

interface RawWaterbody {
  id: string
  name: string
  area: number | null
  location: { coordinates: [number, number] } | null
  latestFishSurveyYear: number | null
  trophyFish: { slug: string }[] | null
  abundantFish: { slug: string }[] | null
  keeperFish: { slug: string }[] | null
  highStockingEffortFish: { slug: string }[] | null
}

type PotentialRawField =
  | 'trophyFish'
  | 'abundantFish'
  | 'keeperFish'
  | 'highStockingEffortFish'

const POTENTIAL_RAW_FIELDS: Record<PotentialCategory, PotentialRawField> = {
  trophy: 'trophyFish',
  abundant: 'abundantFish',
  keeper: 'keeperFish',
  highStockingEffort: 'highStockingEffortFish',
}

const formatDistance = (meters: number) =>
  getBestFitUnitOfLength({
    length: meters,
    from: 'meters',
    unitType: getItem<UnitType>('unitType') ?? 'imperial',
  })

const potentialMatchesFor = (
  raw: RawWaterbody,
  requestedPotential: PotentialCategory[]
) =>
  requestedPotential
    .map((category) => ({
      category,
      label: POTENTIAL_LABELS[category],
      species: (raw[POTENTIAL_RAW_FIELDS[category]] ?? []).map(
        (fishType) => fishType.slug
      ),
    }))
    .filter((match) => match.species.length > 0)

const toResult = (
  raw: RawWaterbody,
  anchor: Anchor | null,
  requestedPotential: PotentialCategory[]
) => {
  const coordinates = raw.location?.coordinates
  const lon = coordinates?.[0]
  const lat = coordinates?.[1]
  const hasCoordinate = typeof lon === 'number' && typeof lat === 'number'

  const distanceMeters =
    anchor && hasCoordinate
      ? getDistance(
          { latitude: anchor.lat, longitude: anchor.lon },
          { latitude: lat, longitude: lon }
        )
      : null
  const direction =
    anchor && hasCoordinate
      ? getCompassDirection(
          { latitude: anchor.lat, longitude: anchor.lon },
          { latitude: lat, longitude: lon }
        )
      : null

  return {
    id: raw.id,
    name: raw.name,
    coordinates,
    // A real, absolute onX URL (see `navigation.ts`'s `buildWaterbodyUrl`) --
    // `citation-markdown-plugin`'s rehype plugin recognizes it (via the
    // matching `parseWaterbodyUrl`) and replaces it with a real, clickable
    // result chip as part of the markdown render itself. Real rather than
    // an inert scheme so copying the message text elsewhere still carries a
    // working link (matching Hunt's existing pattern, webmap-onxmaps#13370).
    url: buildWaterbodyUrl(raw.id),
    areaAcres:
      raw.area === null || raw.area === undefined
        ? null
        : Math.round(sqMetersToAcres(raw.area)),
    latestFishSurveyYear: raw.latestFishSurveyYear ?? null,
    potentialMatches: potentialMatchesFor(raw, requestedPotential),
    distanceMeters,
    distanceText:
      distanceMeters === null ? null : formatDistance(distanceMeters),
    direction: direction ?? null,
  }
}

export const TOOL_QUERY_WATERBODIES: ToolRegistryEntry<AiDependencies> = {
  name: NAME,
  build: (deps: AiDependencies) => {
    const resolveAnchor = (near?: {
      lat: number
      lon: number
    }): Anchor | null => {
      if (near) {
        return { lat: near.lat, lon: near.lon }
      }
      const query = queryCoord()
      if (query && isPointInView(query)) {
        return { lat: query.latitude, lon: query.longitude }
      }
      const center = deps.viewer?.getCenter()
      return center ? { lat: center[1], lon: center[0] } : null
    }

    return createInlineTool({
      name: NAME,
      title: 'Researching waterbodies',
      description: DESCRIPTION,
      inputSchema: {
        species: z
          .array(z.string())
          .optional()
          .describe(
            'Fish type slugs to filter by, e.g. "walleye", "northern-pike".'
          ),
        potential: z
          .array(z.enum(['trophy', 'abundant', 'keeper', 'highStockingEffort']))
          .optional()
          .describe(
            'Which fish-attribute dimension(s) matter, scoped to the given species. Only used when species is also given. See the tool description for what each value means.'
          ),
        near: z
          .object({
            lat: z.number(),
            lon: z.number(),
            radiusMiles: z
              .number()
              .min(0)
              .optional()
              .describe('Search radius in miles. Defaults to 50.'),
          })
          .optional()
          .describe('Where to search from. Omit to search anywhere.'),
        minAreaAcres: z.number().min(0).optional(),
        maxAreaAcres: z.number().min(0).optional(),
      },
      handler: async (args) => {
        const anchor = resolveAnchor(args.near)
        const filter = buildWaterbodyFilter(args)

        let data: { waterbodies: RawWaterbody[] } | null
        try {
          const { data: queryData, error } = await getApolloClient().query({
            query: getWaterbodiesByFilter(),
            variables: { filter, limit: LIMIT },
            fetchPolicy: 'network-only',
          })
          if (error) {
            reportAiError(error, { surface: 'tool', tool: NAME })
            return {
              content: [
                {
                  type: 'text' as const,
                  text: 'The waterbody search could not be completed (the data source returned an error). Tell the user the search failed rather than implying none exist.',
                },
              ],
              isError: true,
            }
          }
          data = queryData
        } catch (error) {
          reportAiError(error, { surface: 'tool', tool: NAME })
          return {
            content: [
              {
                type: 'text' as const,
                text: 'Unable to run the waterbody search right now. Let the user know and offer to try again.',
              },
            ],
            isError: true,
          }
        }

        // Mirrors buildWaterbodyFilter's gating: potential only applies
        // alongside a species filter, matching the map-filter UI's rule
        // that these toggles require an active species selection.
        const requestedPotential = args.species?.length
          ? (args.potential ?? [])
          : []
        const results = (data?.waterbodies ?? []).map((raw) =>
          toResult(raw, anchor, requestedPotential)
        )

        if (results.length === 0) {
          return {
            content: [
              {
                type: 'text' as const,
                text: 'No waterbodies matched those filters. Tell the user nothing matched and offer to widen the search.',
              },
            ],
            isError: false,
            structuredContent: { results },
          }
        }

        return {
          content: [
            { type: 'text' as const, text: JSON.stringify({ results }) },
          ],
          isError: false,
          // Host/UI-only: never forwarded to the LLM (the JSON text part
          // above is what the model reasons over). The citation
          // markdown-plugin (`lib/citation-markdown-plugin`) scans this off
          // the conversation store to build its id -> display-data cache,
          // without a second network round-trip for data the tool already
          // fetched once.
          structuredContent: { results },
        }
      },
    })
  },
}
